import { audioHub, type AudioHub } from './audio/audio-hub.js';
import { createNoiseBuffer, playBell, playNoise, playTone } from './audio/sfx.js';

/**
 * Interface feedback for menus, garage, lobby and the pause screen. Every cue
 * is short, soft and pitched into the score's A-minor family so clicking
 * around never fights the music. Sounds go through the shared hub (UI bus),
 * so they honour mute, the interface volume and the mastering chain.
 */
export class UiSounds {
  private lastHoverAt = 0;
  private lastSliderAt = 0;
  private lastTickAt = 0;

  constructor(private readonly hub: AudioHub = audioHub) {}

  /** Controller/keyboard focus moving between items. */
  tick(): void {
    const now = performance.now();
    if (now - this.lastTickAt < 35) return;
    this.lastTickAt = now;
    this.play((context, ui, noise) => {
      playTone(context, ui, { from: 2_640 + Math.random() * 60, duration: 0.03, gain: 0.03, attack: 0.001 });
      playNoise(context, noise, ui, { duration: 0.012, gain: 0.02, filter: { type: 'highpass', from: 5_000 } });
    });
  }

  /** Pointer hover over a button; throttled so sweeping the mouse stays calm. */
  hover(): void {
    const now = performance.now();
    if (now - this.lastHoverAt < 60) return;
    this.lastHoverAt = now;
    this.play((context, ui) => {
      playTone(context, ui, { from: 1_980, to: 2_200, duration: 0.025, gain: 0.014, attack: 0.002 });
    });
  }

  click(): void {
    this.play((context, ui, noise) => {
      playNoise(context, noise, ui, { duration: 0.018, gain: 0.07, filter: { type: 'bandpass', from: 3_800, q: 1.2 } });
      playTone(context, ui, { from: 1_320, to: 990, duration: 0.06, gain: 0.05, attack: 0.001 });
    });
  }

  confirm(): void {
    this.play((context, ui, noise, reverb) => {
      const at = context.currentTime;
      playNoise(context, noise, ui, { at, duration: 0.22, attack: 0.06, gain: 0.04, filter: { type: 'bandpass', from: 900, to: 5_200, q: 1 } });
      playBell(context, ui, 880, { at, duration: 0.4, gain: 0.05, pan: -0.12, send: reverb, sendGain: 0.25 });
      playBell(context, ui, 1_318.51, { at: at + 0.065, duration: 0.55, gain: 0.05, pan: 0.12, send: reverb, sendGain: 0.25 });
    });
  }

  back(): void {
    this.play((context, ui) => {
      const at = context.currentTime;
      playTone(context, ui, { at, from: 1_174.66, duration: 0.07, gain: 0.04, type: 'triangle' });
      playTone(context, ui, { at: at + 0.05, from: 880, duration: 0.1, gain: 0.04, type: 'triangle' });
    });
  }

  toggle(on: boolean): void {
    this.play((context, ui, noise) => {
      playNoise(context, noise, ui, { duration: 0.015, gain: 0.05, filter: { type: 'bandpass', from: on ? 4_200 : 2_600, q: 1.5 } });
      playTone(context, ui, { from: on ? 1_320 : 990, to: on ? 1_760 : 740, duration: 0.07, gain: 0.04, type: 'triangle' });
    });
  }

  /** Live slider feedback; pitch follows the value (0..1). */
  slider(value: number): void {
    const now = performance.now();
    if (now - this.lastSliderAt < 45) return;
    this.lastSliderAt = now;
    const clamped = Math.max(0, Math.min(1, value));
    this.play((context, ui) => {
      playTone(context, ui, { from: 520 * 2 ** (clamped * 1.5), duration: 0.05, gain: 0.03 + clamped * 0.02, type: 'triangle' });
    });
  }

  error(): void {
    this.play((context, ui) => {
      const at = context.currentTime;
      for (const offset of [0, 0.11]) {
        playTone(context, ui, {
          at: at + offset, from: 220, duration: 0.09, gain: 0.04, type: 'square',
          filter: { type: 'lowpass', frequency: 1_400 },
        });
      }
    });
  }

  /** Another driver arrived in the lobby: a bright rising pair (A5 → E6). */
  playerJoined(): void {
    this.play((context, ui, _noise, reverb) => {
      const at = context.currentTime;
      playBell(context, ui, 880, { at, duration: 0.32, gain: 0.04, pan: -0.2, send: reverb, sendGain: 0.3 });
      playBell(context, ui, 1_318.51, { at: at + 0.09, duration: 0.5, gain: 0.045, pan: 0.2, send: reverb, sendGain: 0.3 });
    });
  }

  /** A driver left: the same pair, falling and darker. */
  playerLeft(): void {
    this.play((context, ui) => {
      const at = context.currentTime;
      playTone(context, ui, { at, from: 987.77, duration: 0.09, gain: 0.03, type: 'triangle' });
      playTone(context, ui, { at: at + 0.08, from: 659.25, duration: 0.16, gain: 0.03, type: 'triangle' });
    });
  }

  /** Someone else confirmed ready; a soft lock-in click with a short tail. */
  playerReady(): void {
    this.play((context, ui, noise, reverb) => {
      const at = context.currentTime;
      playNoise(context, noise, ui, { at, duration: 0.02, gain: 0.05, filter: { type: 'bandpass', from: 3_200, q: 1.4 } });
      playBell(context, ui, 1_760, { at: at + 0.01, duration: 0.28, gain: 0.03, send: reverb, sendGain: 0.2 });
    });
  }

  /** A short preview so a volume change is heard immediately. */
  previewEffects(): void {
    this.play((context, _ui, noise) => {
      const effects = this.hub.outputs?.effects;
      if (!effects) return;
      const at = context.currentTime;
      playTone(context, effects, { at, from: 90, to: 40, duration: 0.25, gain: 0.25 });
      playNoise(context, noise, effects, { at, duration: 0.18, gain: 0.12, filter: { type: 'lowpass', from: 2_400, to: 300 } });
    });
  }

  private play(render: (context: AudioContext, ui: AudioNode, noise: AudioBuffer, reverb: AudioNode) => void): void {
    if (this.hub.isMuted || this.hub.currentMix.ui <= 0) return;
    const context = this.hub.ensure();
    const buses = this.hub.outputs;
    if (!context || !buses) return;
    try {
      const noise = this.hub.buffer('noise', createNoiseBuffer);
      if (!noise) return;
      render(context, buses.ui, noise, buses.reverb);
    } catch {
      // A closing or device-less context must never break a button press.
    }
  }
}
